/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { useEffect, useState, type ReactNode } from "react";
import { useMediaQuery } from "../useMediaQuery";
import styles from "./FullscreenCallControls.module.css";

/** The ordinary call footer is unchanged outside fullscreen screen sharing. */
export function FullscreenCallControls({
  active,
  children,
}: {
  active: boolean;
  children: ReactNode;
}) {
  const touch = useMediaQuery("(hover: none), (pointer: coarse)");
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    if (!active) return;
    setVisible(true);
    if (touch) return;
    let timeout = window.setTimeout(() => setVisible(false), 2500);
    const activity = (): void => {
      window.clearTimeout(timeout);
      setVisible(true);
      timeout = window.setTimeout(() => setVisible(false), 2500);
    };
    window.addEventListener("pointermove", activity);
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("pointermove", activity);
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
    };
  }, [active, touch]);
  if (!active) return <>{children}</>;
  return (
    <div className={styles.overlay} data-visible={visible}>
      {children}
    </div>
  );
}
